import mammoth from 'mammoth';
import { LanguagesSupported } from '../components/constants';

export type StaticContentType = 'faq' | 'thoughtstarters';

const STATIC_CONTENT_FOLDERS: Record<StaticContentType, string> = {
    faq: 'faq',
    thoughtstarters: 'ts-docs',
};

const SUPPORTED_LANGUAGE_CODES = new Set(
    LanguagesSupported.map((language) => language.value)
);

const getLanguageCode = (language: string): string => {
    if (!language) {
        return 'en';
    }

    return SUPPORTED_LANGUAGE_CODES.has(language) ? language : 'en';
};

const getStaticContentPath = (contentType: StaticContentType, language: string): string => {
    return `/${STATIC_CONTENT_FOLDERS[contentType]}/${language}.html`;
};

export const loadStaticHtmlContent = async (
    contentType: StaticContentType,
    language: string
): Promise<string> => {
    const selectedLanguage = getLanguageCode(language);
    const cacheKey = `static-content-${contentType}-${selectedLanguage}`;
    const cachedContent = sessionStorage.getItem(cacheKey);

    if (cachedContent) {
        return cachedContent;
    }

    // Try .docx first (prefer docx sources), then fall back to .html files
    const docxPath = `/${STATIC_CONTENT_FOLDERS[contentType]}/${contentType}-${selectedLanguage}.docx`;
    const htmlPath = getStaticContentPath(contentType, selectedLanguage);

    // helper to post-process mammoth HTML for known content types
    const postProcess = (rawHtml: string) => {
        if (contentType === 'faq') {
            let html = rawHtml.replace(/^<p>/, '<p class="faq-title">');
            html = html.replace(/<p>([^<]*\?\s*)<\/p>/g, '<p class="faq-question">$1</p>');
            return html;
        }

        if (contentType === 'thoughtstarters') {
            let html = rawHtml.replace(/^<p>/, '<p class="ts-title">');           // main title
            html = html.replace(/<p>/, '<p class="ts-subtitle">');                 // centered subtitle
            html = html.replace(/<p>((?:Thought Starters|Example Nomination|THOUGHT STARTERS|EXAMPLE)[^<]*)<\/p>/gi, '<p class="ts-section-title">$1</p>');
            return html;
        }

        return rawHtml;
    };

    // Fetch DOCX and HTML in parallel to avoid waiting on sequential 404s
    try {
        console.debug(`Attempting parallel fetch for: ${docxPath} and ${htmlPath}`);
        const docxFetch = fetch(docxPath, { cache: 'no-cache' }).catch(() => null);
        const htmlFetch = fetch(htmlPath, { cache: 'no-cache' }).catch(() => null);

        const [docxResp, htmlResp] = await Promise.all([docxFetch, htmlFetch]);

        if (docxResp && docxResp.ok) {
            const ab = await docxResp.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer: ab });
            let html = postProcess(result.value);
            console.debug(`DOCX converted, content length ${html.length}`);
            console.debug(`DOCX snippet: ${html.slice(0,200)}`);
            sessionStorage.setItem(cacheKey, html);
            return html;
        }

        if (htmlResp && htmlResp.ok) {
            const html = await htmlResp.text();
            sessionStorage.setItem(cacheKey, html);
            return html;
        }
    } catch (e) {
        console.debug('Parallel fetch error', e);
    }

    // Fallback: try English HTML (handles case where language-specific assets are missing)
    const pathsToTry = selectedLanguage === 'en'
        ? [getStaticContentPath(contentType, 'en')]
        : [getStaticContentPath(contentType, 'en')];

    for (const path of pathsToTry) {
        try {
            const response = await fetch(path, { cache: 'no-cache' });
            if (!response.ok) continue;
            const html = await response.text();
            sessionStorage.setItem(cacheKey, html);
            return html;
        } catch (e) {
            continue;
        }
    }

    throw new Error(`Static ${contentType} content could not be loaded for language ${selectedLanguage}.`);
};

export default loadStaticHtmlContent;
